import { DomFlags } from "@tento-ui/dom-flags";
import { Flags } from "@tento-ui/flags";
import { RenderFlags } from "@tento-lona/sheets";

export namespace LonaFeatureFlags {
  export namespace debug {
    export const noCalendars = new Flags.Debug(
      "no-calendars",
      false,
      "Hide all calendars",
    );
    export const fastCurrentTime = new Flags.Debug(
      "events-fast-current-time",
      false,
      "Speed up current-time indicator",
    );
    export const eventsPluginTzBg = new Flags.Debug(
      "events-plugin-tz-bg",
      false,
      "Show timezone background bands",
    );
    export const sheetPropertyRow = new Flags.Debug(
      "sheet-property-row",
      false,
      "Show sheet property rows",
    );
    export const fakeChartData = new Flags.Debug(
      "fake-chart-data",
      false,
      "Use fake chart data",
    );
    export const scrollInfo = new Flags.Debug(
      "scroll-info",
      false,
      "Show scroll debug info",
    );
    export const scrollPopover = new Flags.Debug(
      "scroll-popover-space-remaining",
      false,
      "Show popover space remaining",
    );
    export const tableBg = new Flags.Debug(
      "table-bg",
      false,
      "Show table background",
    );
    export const selfPresence = new Flags.Debug(
      "self-presence",
      false,
      "Show own presence cursor",
    );
    export const sheetLayoutDebugOverlay = new Flags.Debug(
      "sheet-layout-debug-overlay",
      false,
      "Show sheet layout debug overlay",
    );
    export const sheetDecorationFixedFrame = new Flags.Debug(
      "sheet-decoration-fixed-frame",
      false,
      "Show decoration fixed frame",
    );
    export const useLegacyToaster = new Flags.Debug(
      "use-legacy-toaster",
      false,
      "Use legacy toaster",
    );
    export const toasterOutline = new Flags.Debug(
      "toaster-outline",
      false,
      "Show toaster outline",
    );
    export const nuxCompletedOn = new Flags.Debug(
      "nux-completed-on",
      false,
      "Mark NUX as completed",
    );
    export const nuxForceNew = new Flags.Debug(
      "nux-force-new",
      false,
      "Force new-user state (fresh cards + intro)",
    );
    export const nuxHide = new Flags.Debug(
      "nux-hide",
      false,
      "Clean-home screenshots: activity subtitle + hide only the tour card",
    );
    export const dataHandleDelay = Flags.Development.dataHandleDelay;
    export const sheetNavigationDelay = new Flags.Debug(
      "sheet-navigation-delay",
      false,
      "Add delay to sheet navigation",
    );
    export const gracePeriodBanner = new Flags.Debug(
      "grace-period-banner",
      false,
      "Show grace period banner",
    );
    export const yearTimeScale = new Flags.Debug(
      "year-time-scale",
      false,
      "Enable year time scale",
    );
    export const subscriptionDrawer = new Flags.Debug(
      "subscription-drawer",
      false,
      "Show subscription drawer",
    );
    export const chatBubbleBoundaries = new Flags.Debug(
      "chat-bubble-boundaries",
      false,
      "Show chat bubble boundaries",
    );
    export const fakeCalendars = new Flags.Debug(
      "fake-calendars",
      false,
      "Show 60 fake calendars",
    );
    export const timelineLegendRow = new Flags.Debug(
      "timeline-legend-row",
      false,
      "Add a timeline legend row to footer",
    );
    export const syncPollToast = new Flags.Debug(
      "sync-poll-toast",
      false,
      "Show sync poll debug toast",
    );
    export const columnRebindToast = new Flags.Debug(
      "sheet-column-rebind-toast",
      false,
      "Show sheet column rebind debug toast",
    );
    export const sheetCowToast = new Flags.Debug(
      "sheet-cow-toast",
      false,
      "Show sheet COW debug toast",
    );
    export const opsRecorder = new Flags.Debug(
      "ops-recorder",
      false,
      "Record row writes and view gestures as handle-ops (window.__lonaOpsRecording(); replay: lona --dev sheets.ops)",
    );
    export const pomodoroDebugTimer = new Flags.Debug(
      "pomodoro-debug-timer",
      false,
      "Show 5-second debug timer in pomodoro",
    );
    export const legendMeasureToast = new Flags.Debug(
      "legend-measure-toast",
      false,
      "Show legend width measurement toast",
    );
    export const legendMeasureSentinel = new Flags.Debug(
      "legend-measure-sentinel",
      false,
      "Run sentinel DOM measurement and compare against pretext",
    );
    export const outlookProvider = new Flags.Debug(
      "outlook-provider",
      false,
      "Show Outlook provider surfaces (connect card + Microsoft signin)",
    );
    /**
     * Per-device override of the SDK 2.0 render pipeline. Seeded from
     * `RenderFlags.defaultLive()` — the single source of truth in
     * `@tento-lona/sheets` — so a device that has never touched the spotlight
     * toggle follows whatever shipped, and toggling it pins this device on or
     * off in either direction.
     */
    export const renderNext = new Flags.Debug(
      "render-next",
      RenderFlags.defaultLive(),
      "Toggle SDK 2.0 render pipeline (scene commit + cell directives + LR6 skip)",
    );
    /**
     * Verbose per-bind measurement instrumentation for the SDK 2.0 pipeline —
     * op-list assembly, per-column shape breadcrumbs, happy-path referee spam.
     * Separate from `renderNext` because that flag is no longer a proxy for "is
     * this a measurement session" once the pipeline ships on.
     */
    export const renderNextDiagnostics = new Flags.Debug(
      "render-next-diagnostics",
      false,
      "SDK 2.0 render pipeline: verbose per-bind measurement breadcrumbs",
    );
    /**
     * Project sheet-scroll-smoothness: no-delay content binding. While the
     * sheet moves, content binds every frame up to a wall-clock deadline,
     * visible columns first; nothing waits for settle. Read once per page
     * load by `ScrollHorizontal.frameDeadlineMs()` in tento-ui. Default on
     * since dom-perf 49 (the operator's flip of 2026-10-12); off restores the
     * settled drain.
     */
    export const scrollFrameDeadline = new Flags.Debug(
      "scroll-frame-deadline",
      true,
      "Scroll: bind content every frame up to the frame deadline, visible columns first (on by default; off: the settled drain; budget: scroll-frame-deadline-ms, default 6) (reload to apply)",
      "dom",
    );
    /**
     * Project sheet-scroll-smoothness: `DomUtils.clearChildren` and component
     * recycling (tento-ui) wrap their DOM clears in performance marks and a
     * caller-named measure for a devtools timeline recording. Off by default:
     * nothing but a live recording reads them, and making them cost ~50 ms
     * per scroll burst. Read once per page load by
     * `DomUtils.timingMarksEnabled()`.
     */
    export const domTimingMarks = new Flags.Debug(
      "dom-timing-marks",
      false,
      "DOM: performance marks around clear-children and recycling, for timeline recordings (reload to apply)",
      "dom",
    );

    /**
     * The DOM's other levers, read through `DomFlags` (tento-ui-js
     * dom-flags.ts) from the same `debug-flag:` storage key this registry
     * writes. Each starts at the value the DOM falls back to, so the menu
     * shows what a device that never toggled it runs; most are A/B arms of
     * project sheet-scroll-smoothness and dom-perf, off by default. The
     * whole-number levers have no row (a debug toggle is a boolean): they are
     * `DOM_COUNT_LEVERS`.
     */
    export const scrollColumnRetention = new Flags.Debug(
      "scroll-column-retention",
      true,
      "Scroll: keep recycled columns with their content for a fling back (on by default; spares: scroll-column-retention-spares, default 20) (reload to apply)",
      "dom",
    );
    export const scrollParkHostOnly = new Flags.Debug(
      "scroll-park-host-only",
      true,
      "Scroll: a park mark hides a retained column with host-only properties, so parking restyles the host alone (on by default) (reload to apply)",
      "dom",
    );
    export const calendarDatedReadReuse = new Flags.Debug(
      "calendar-dated-read-reuse",
      true,
      "Calendar: reuse event reads across changes that cannot touch their days (on by default) (reload to apply)",
      "dom",
    );
    export const calendarIdleComplete = new Flags.Debug(
      "calendar-idle-complete",
      true,
      "Calendar: a parked strip finishes its bind in idle time (on by default) (reload to apply)",
      "dom",
    );
    export const calendarLazyEditorMount = new Flags.Debug(
      "calendar-lazy-editor-mount",
      true,
      "Calendar: text cells bind static markup and mount their editor when reached or in idle time (on by default) (reload to apply)",
      "dom",
    );
    export const calendarRoughLayer = new Flags.Debug(
      "calendar-rough-layer",
      false,
      "Calendar: while a column's events build, blurred boxes show where they will be (reload to apply)",
      "dom",
    );
    export const calendarRoughShimmer = new Flags.Debug(
      "calendar-rough-shimmer",
      false,
      "Calendar: while a column loads, a shimmer sweeps across it until its events reveal (reload to apply)",
      "dom",
    );
    export const calendarBindPace = new Flags.Debug(
      "calendar-bind-pace",
      false,
      "Calendar: record the bind-pace trace of content lag (reload to apply)",
      "dom",
    );
    export const calendarContentStates = new Flags.Debug(
      "calendar-content-states",
      false,
      "Calendar: record what each visible day shows while scrolling (content states trace; reload to apply)",
      "dom",
    );
    export const stripBudget = new Flags.Debug(
      "strip-budget",
      true,
      "Calendar: strips build under the frame budget; off binds every day in one task (reload to apply)",
      "dom",
    );
    export const rowMembershipPass = new Flags.Debug(
      "row-membership-pass",
      true,
      "Sheet: a pass after only a row-viewport membership change keeps the cells that still show their rows (reload to apply)",
      "dom",
    );
    export const rowDemand = new Flags.Debug(
      "row-demand",
      false,
      "Sheet: the rendered emit's per-row pass follows row demand (reload to apply)",
      "dom",
    );
    export const frameMarks = new Flags.Debug(
      "frame-marks",
      false,
      "Perf: record per-frame performance measures for a frame-anatomy recording (reload to apply)",
      "dom",
    );
    export let betaFeatures: Option<Flags.Debug>;

    export const ALL_FLAGS: Flags.Debug[] = [
      noCalendars,
      fastCurrentTime,
      eventsPluginTzBg,
      sheetPropertyRow,
      fakeChartData,
      scrollInfo,
      scrollPopover,
      tableBg,
      selfPresence,
      sheetLayoutDebugOverlay,
      sheetDecorationFixedFrame,
      useLegacyToaster,
      toasterOutline,
      nuxCompletedOn,
      nuxForceNew,
      sheetNavigationDelay,
      gracePeriodBanner,
      yearTimeScale,
      subscriptionDrawer,
      chatBubbleBoundaries,
      fakeCalendars,
      timelineLegendRow,
      syncPollToast,
      columnRebindToast,
      sheetCowToast,
      opsRecorder,
      pomodoroDebugTimer,
      legendMeasureToast,
      legendMeasureSentinel,
      outlookProvider,
      renderNext,
      renderNextDiagnostics,
      scrollFrameDeadline,
      domTimingMarks,
      scrollColumnRetention,
      scrollParkHostOnly,
      calendarDatedReadReuse,
      calendarIdleComplete,
      calendarLazyEditorMount,
      calendarRoughLayer,
      calendarRoughShimmer,
      calendarBindPace,
      calendarContentStates,
      stripBudget,
      rowMembershipPass,
      rowDemand,
      frameMarks,
      ...Flags.Development.ALL_FLAGS,
    ];
  }

  /**
   * The DOM's whole-number levers (`DomFlags.readCount`), which have no
   * debug-menu row: each with the boolean row that switches it on and the
   * value the DOM falls back to (`flags-dom-registry.deno.test.ts` pins both
   * to the reader).
   */
  export const DOM_COUNT_LEVERS: Readonly<
    Record<string, { readonly switch: string; readonly fallback: number; readonly description: string }>
  > = Object.freeze({
    "scroll-column-retention-spares": {
      switch: "scroll-column-retention",
      fallback: 20,
      description:
        "Scroll: K, the parked-column count (ScrollHorizontal.COLUMN_RETENTION_SPARES); read only while retention is on (reload to apply)",
    },
    "scroll-frame-deadline-ms": {
      switch: "scroll-frame-deadline",
      fallback: 6,
      description:
        "Scroll: the content budget per motion frame in ms (ScrollHorizontal.FRAME_DEADLINE_MS); read only while the deadline is on (reload to apply)",
    },
  });

  /**
   * The performance levers whose recommended value is not the DOM's default,
   * by key. Empty: every recommended value became the default on 2026-10-12
   * (dom-perf 49, DOM 69), so the recommended set is every lever at its
   * default. A recommendation that differs from a default is declared here.
   */
  export const PERF_RECOMMENDATIONS: Readonly<Record<string, boolean | number>> = Object.freeze({});

  /**
   * Where the value a lever runs on this page comes from: a harness override,
   * `?flag:<key>`, `debug-flag:<key>` storage, or the DOM's default
   * (`DomFlags.Source`).
   */
  export type PerfSource = DomFlags.Source;

  /**
   * One performance lever in the profile: what is recommended, the DOM's
   * default (`fallback`: it differs from `recommended` only for a lever in
   * `PERF_RECOMMENDATIONS`), what this page runs (`current`, from `source`),
   * whether they match, and what the next load reads (`onReload`: the URL and
   * storage now, which differ from `current` after a toggle stored since the
   * page read the lever).
   */
  export type PerfProfileRow = {
    readonly key: string;
    readonly description: string;
    readonly recommended: boolean | number;
    readonly fallback: boolean | number;
    readonly current: boolean | number;
    readonly source: PerfSource;
    readonly matches: boolean;
    readonly onReload: DomFlags.Read<boolean | number>;
  };

  /** How the profile reads a lever: `DomFlags`' own answer (`describe` / `describeCount`), the one read rule. */
  export type PerfReader = Pick<typeof DomFlags, "describe" | "describeCount">;

  /**
   * The perf flag profile (operator 2026-10-16): one row per DOM lever, the
   * boolean rows of `ALL_FLAGS` with `layer: "dom"` then `DOM_COUNT_LEVERS`,
   * each with its recommended value (`recommendations`, else the DOM's
   * default) and what this page runs, as `DomFlags` answers it (DOM judge r26
   * R26-N2: the profile does not re-read the URL or storage itself).
   */
  export function perfProfile(
    reader: PerfReader = DomFlags,
    recommendations: Readonly<Record<string, boolean | number>> = PERF_RECOMMENDATIONS,
  ): PerfProfileRow[] {
    const row = (
      key: string,
      description: string,
      fallback: boolean | number,
      described: DomFlags.Description<boolean | number>,
    ): PerfProfileRow => {
      const recommended = recommendations[key] ?? fallback;
      const { value: current, source, onReload } = described;
      return { key, description, recommended, fallback, current, source, matches: current === recommended, onReload };
    };
    return [
      ...debug.ALL_FLAGS
        .filter((flag) => flag.layer === "dom")
        .map((flag) =>
          row(flag.key, flag.description ?? "", flag.defaultValue, reader.describe(flag.key, flag.defaultValue))
        ),
      ...Object.entries(DOM_COUNT_LEVERS)
        .map(([key, lever]) => row(key, lever.description, lever.fallback, reader.describeCount(key, lever.fallback))),
    ];
  }

  /** How many of the profile's rows match their recommendation. */
  export function perfProfileSummary(rows: readonly PerfProfileRow[]): { matching: number; total: number } {
    return { matching: rows.filter((row) => row.matches).length, total: rows.length };
  }

  /** One stored override "Perf flags: apply recommended" writes: a lever and its recommended value. */
  export type PerfWrite = { readonly key: string; readonly value: boolean | number; readonly clears: boolean };

  /**
   * What "Perf flags: apply recommended" does to this browser (operator
   * decision Q8, 2026-10-17): `writes` holds the recommended value of each
   * lever the next load would not read at its recommendation (`onReload`), in
   * profile order; `fromUrl` names those a `?flag:` parameter holds, which
   * beats storage, so no write could make them match.
   */
  export type PerfApplyPlan = { readonly writes: readonly PerfWrite[]; readonly fromUrl: readonly string[] };

  /**
   * The apply plan for `rows`. A lever a reload already reads at its
   * recommendation gets no write, so the store gains no override that
   * changes nothing; one whose page value differs only until reload (a toggle
   * stored since load) is judged by what the reload reads.
   */
  export function perfApplyPlan(rows: readonly PerfProfileRow[]): PerfApplyPlan {
    const off = rows.filter((row) => row.onReload.value !== row.recommended);
    return {
      writes: off
        .filter((row) => row.onReload.source !== "url")
        .map((row) => ({ key: row.key, value: row.recommended, clears: row.recommended === row.fallback })),
      fromUrl: off.filter((row) => row.onReload.source === "url").map((row) => row.key),
    };
  }

  /**
   * The apply confirmation's text: how many flags it writes and how many it
   * clears, then each write as `key=value` and each clear as `key (reads its
   * default value)`, in plan order, then the URL-held levers. A clear is
   * named as one; "Writes 3 flags" would hide a plan that removes three keys.
   */
  export function perfApplySummary(plan: PerfApplyPlan): string {
    const fromUrl = plan.fromUrl.length === 0
      ? ""
      : ` Set by the URL, not changed: ${plan.fromUrl.map((key) => `?flag:${key}`).join(", ")}.`;
    if (plan.writes.length === 0) {
      return fromUrl === "" ? "Nothing to write: a reload reads every perf flag at its recommended value." : `Nothing to write.${fromUrl}`;
    }
    const flags = (count: number) => `${count} ${count === 1 ? "flag" : "flags"}`;
    const stored = plan.writes.filter((write) => !write.clears).length;
    const cleared = plan.writes.length - stored;
    const head = stored === 0
      ? `Clears ${flags(cleared)}`
      : cleared === 0
      ? `Writes ${flags(stored)}`
      : `Writes ${flags(stored)} and clears ${flags(cleared)}`;
    const writes = plan.writes
      .map((write) => write.clears ? `${write.key} (reads its default ${write.value})` : `${write.key}=${write.value}`)
      .join(", ");
    return `${head} in this browser, then reloads: ${writes}.${fromUrl}`;
  }

  /** Where a debug flag's per-browser value lives: `debug-flag:<key>`, as `Flags.Debug` and `DomFlags` read it. */
  export type FlagStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

  const STORAGE_PREFIX = "debug-flag:";

  /**
   * The keys the recommended debug performance flag set once wrote (operator
   * 2026-10-10, "Perf flags: apply recommended set"; the dom-perf
   * recommendation, claude-takeover/dom/perf/flag-default-recommendation.md).
   * The operator flipped every one of them to the DOM's default on 2026-10-12
   * (the joint scroll set, K = 20 and motion strip-first: dom-perf 49;
   * light-DOM cells and chips: DOM 69) and turned motion strip-first back off
   * the same day (dom-perf 51; removed under the OFF-lever rule, DOM 73),
   * so the set is empty and that command is gone (its successor, "Perf
   * flags: apply recommended", writes only what `perfApplyPlan` names); a browser that applied the set still stores these
   * overrides, and each is read at boot, so the spotlight's "Perf flags:
   * reset to defaults" clears them (`resetPerfFlagOverrides`). A key whose
   * lever was removed since stays, so the reset still clears its stale
   * override. The list is historical: a key is never added for a new lever.
   */
  export const PERF_FLAG_OVERRIDE_KEYS: readonly string[] = Object.freeze([
    "scroll-frame-deadline",
    "scroll-column-retention",
    "scroll-column-retention-spares",
    "calendar-dated-read-reuse",
    "calendar-idle-complete",
    "scroll-park-host-only",
    "scroll-park-content-visibility",
    "calendar-lazy-editor-mount",
    "calendar-light-dom-cells",
    "scroll-motion-strip-first",
  ]);

  /** Each historical key with the value this browser stores for it; null means unset (the default applies). */
  export function readPerfFlagOverrides(
    storage: FlagStorage = localStorage,
  ): [key: string, stored: string | null][] {
    return PERF_FLAG_OVERRIDE_KEYS.map((key) => [key, storage.getItem(STORAGE_PREFIX + key)]);
  }

  /** Clears `keys` (every historical key unless told), so each reads its default again; other flags keep what they hold. Applies on reload. */
  export function resetPerfFlagOverrides(
    storage: FlagStorage = localStorage,
    keys: readonly string[] = PERF_FLAG_OVERRIDE_KEYS,
  ): void {
    for (const key of keys) storage.removeItem(STORAGE_PREFIX + key);
  }

  /**
   * Puts each write's lever on its recommendation for the next load: a write
   * whose recommendation is the DOM's default (`clears`) removes the stored
   * `debug-flag:<key>`, so the browser follows the default (and any later
   * default flip) instead of pinning today's value; a declared recommendation
   * that differs from the default is stored as `debug-flag:<key>` (as
   * `Flags.Debug` and `DomFlags` read it). Operator review option a,
   * 2026-10-18, reversing D79-3 (which stored the default). Other keys keep
   * what they hold. Applies on reload.
   */
  export function applyPerfRecommendations(storage: FlagStorage, writes: readonly PerfWrite[]): void {
    for (const write of writes) {
      if (write.clears) storage.removeItem(STORAGE_PREFIX + write.key);
      else storage.setItem(STORAGE_PREFIX + write.key, String(write.value));
    }
  }
}
